import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Loader2, KeyRound, Check, X } from 'lucide-react';

const TICKET_KEY = 'google_link_ticket';

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.27-4.74 3.27-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z" />
  </svg>
);

/**
 * Settings → how they sign in. Lets someone who was set up with an email and password add
 * Google sign-in to the same account, even when their Google address is a different one.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export const SignInMethodPanel = () => {
  const [methods, setMethods] = useState(null);
  const [password, setPassword] = useState('');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/auth/sign-in-methods`)
    .then((r) => setMethods(r.data))
    .catch(() => setMethods({ error: true }));

  useEffect(() => { load(); }, []);

  const startLink = async () => {
    if (!password) return toast.error('Your current password, to be sure it is you');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/google/link/start`, { password });
      sessionStorage.setItem(TICKET_KEY, data.ticket);
      const redirectUrl = `${window.location.origin}/auth/link-google`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That could not be started');
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm('Remove Google sign-in? Your email and password will still work.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/google/unlink`);
      toast.success(data.message);
      setMethods(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That could not be removed');
    } finally {
      setBusy(false);
    }
  };

  if (!methods) {
    return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-white/40" /></div>;
  }
  if (methods.error) return null;

  return (
    <div className="space-y-4" data-testid="sign-in-method-panel">
      <div className="space-y-2">
        <p className="flex items-center gap-2 text-sm text-gray-300" data-testid="sign-in-password-row">
          {methods.has_password
            ? <Check className="h-4 w-4 text-emerald-400" />
            : <X className="h-4 w-4 text-gray-500" />}
          <KeyRound className="h-4 w-4 text-gray-500" />
          Email and password
          <span className="text-xs text-gray-500">— {methods.email}</span>
        </p>
        <p className="flex flex-wrap items-center gap-2 text-sm text-gray-300" data-testid="sign-in-google-row">
          {methods.google_linked
            ? <Check className="h-4 w-4 text-emerald-400" />
            : <X className="h-4 w-4 text-gray-500" />}
          <GoogleMark />
          Google
          {methods.google_email
            ? <span className="text-xs text-gray-500">— {methods.google_email}</span>
            : <span className="text-xs text-gray-500">— not added</span>}
        </p>
      </div>

      {methods.google_linked ? (
        <div className="space-y-2">
          <p className="text-xs text-gray-400">
            Either one gets you in. Nothing to lock yourself out of.
          </p>
          <button onClick={unlink} disabled={busy}
                  className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-gray-300
                             transition-colors hover:border-rose-400/60 hover:text-rose-200
                             disabled:opacity-40"
                  data-testid="unlink-google-btn">
            Remove Google sign-in
          </button>
        </div>
      ) : !asking ? (
        <div className="space-y-2">
          <p className="text-xs text-gray-400">
            Add Google and you can sign in either way — even if your Google address is a
            different one from the email above. Your password keeps working.
          </p>
          <button onClick={() => setAsking(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-white/20
                             bg-white/5 px-4 py-2 text-sm text-white transition-colors
                             hover:border-cyan-300/60"
                  data-testid="add-google-signin-btn">
            <GoogleMark /> Add Google sign-in
          </button>
        </div>
      ) : (
        <div className="space-y-3 rounded-lg border border-cyan-400/30 bg-cyan-400/[0.04] p-4">
          <p className="text-xs text-gray-300">
            Confirm your current password, then choose your Google account.
          </p>
          <input type="password" value={password} autoFocus
                 onChange={(e) => setPassword(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && startLink()}
                 placeholder="Current password"
                 className="w-full max-w-xs rounded-lg border border-white/15 bg-black/30 px-3
                            py-2 text-sm text-white placeholder-white/30 focus:border-cyan-300/60
                            focus:outline-none"
                 data-testid="link-google-password-input" />
          <div className="flex gap-2">
            <button onClick={startLink} disabled={busy}
                    className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2
                               text-sm font-medium text-slate-900 transition-colors
                               hover:bg-gray-100 disabled:opacity-40"
                    data-testid="link-google-continue-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleMark />}
              Continue with Google
            </button>
            <button onClick={() => { setAsking(false); setPassword(''); }}
                    className="rounded-lg px-3 py-2 text-sm text-gray-400 transition-colors
                               hover:text-white"
                    data-testid="link-google-cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignInMethodPanel;
